import React, { useState, useEffect } from 'react';
import { Youtube, Save, Loader2, ExternalLink, Play, AlertCircle } from 'lucide-react';
import { youtubeAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

// Extract video ID from YouTube URL
const extractVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const AdminYouTube = () => {
  const { t } = useLanguage();
  const [videos, setVideos] = useState([
    { id: 'yt1', url: '', title: '', title_tr: '', video_id: '' },
    { id: 'yt2', url: '', title: '', title_tr: '', video_id: '' },
    { id: 'yt3', url: '', title: '', title_tr: '', video_id: '' }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const data = await youtubeAPI.getAll();
      if (data && data.length > 0) {
        // Map fetched data to our state structure
        const mappedVideos = data.slice(0, 3).map((video, idx) => ({
          id: video.id || `yt${idx + 1}`,
          url: `https://www.youtube.com/watch?v=${video.video_id}`,
          title: video.title || '',
          title_tr: video.title_tr || '',
          video_id: video.video_id
        }));
        // Fill remaining slots if less than 3
        while (mappedVideos.length < 3) {
          mappedVideos.push({
            id: `yt${mappedVideos.length + 1}`,
            url: '',
            title: '',
            title_tr: '',
            video_id: ''
          });
        }
        setVideos(mappedVideos);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (index, value) => {
    const newVideos = [...videos];
    newVideos[index] = {
      ...newVideos[index],
      url: value,
      video_id: extractVideoId(value) || ''
    };
    setVideos(newVideos);
  };

  const handleTitleChange = (index, field, value) => {
    const newVideos = [...videos];
    newVideos[index] = {
      ...newVideos[index],
      [field]: value
    };
    setVideos(newVideos);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out empty videos and format for API
      const videosToSave = videos
        .filter(v => v.video_id)
        .map((v, idx) => ({
          id: v.id,
          video_id: v.video_id,
          url: v.url,
          title: v.title,
          title_tr: v.title_tr,
          order: idx
        }));

      await youtubeAPI.update(videosToSave);
      toast.success(t('admin.youtube.saved'));
    } catch (err) {
      console.error('Failed to save videos:', err);
      toast.error(t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-youtube-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: '#F8FAFC' }}>
            <Youtube size={28} style={{ color: '#FF0000' }} />
            {t('admin.youtube.title')}
          </h1>
          <p className="mt-2" style={{ color: '#94A3B8' }}>
            {t('admin.youtube.description')}
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300"
          style={{
            background: saving ? 'rgba(0, 240, 255, 0.3)' : 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)',
            color: '#0F172A'
          }}
          data-testid="save-videos-btn"
        >
          {saving ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              {t('admin.youtube.saving')}
            </>
          ) : (
            <>
              <Save size={20} />
              {t('admin.common.save')}
            </>
          )}
        </button>
      </div>

      {/* Video Cards */}
      <div className="grid gap-8">
        {videos.map((video, index) => (
          <div
            key={video.id}
            className="p-6 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid rgba(0, 240, 255, 0.2)'
            }}
            data-testid={`video-card-${index}`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(255, 0, 0, 0.15)' }}
              >
                <Play size={20} style={{ color: '#FF0000' }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: '#F8FAFC' }}>
                {t('admin.youtube.video')} {index + 1}
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* URL & Preview Column */}
              <div className="space-y-4">
                {/* URL Input */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                    YouTube URL
                  </label>
                  <input
                    type="text"
                    value={video.url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    placeholder={t('admin.youtube.urlPlaceholder')}
                    className="w-full px-4 py-3 rounded-lg transition-all duration-300"
                    style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(0, 240, 255, 0.2)',
                      color: '#F8FAFC'
                    }}
                    data-testid={`video-url-${index}`}
                  />
                  {video.url && !video.video_id && (
                    <div className="flex items-center gap-2 mt-2 text-sm" style={{ color: '#EF4444' }}>
                      <AlertCircle size={14} />
                      <span>Invalid YouTube URL</span>
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                    {t('admin.youtube.preview')}
                  </label>
                  {video.video_id ? (
                    <div className="relative rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                      <img
                        src={`https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`}
                        alt="Video thumbnail"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`;
                        }}
                      />
                      <div 
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ background: 'rgba(0,0,0,0.3)' }}
                      >
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(255, 0, 0, 0.9)' }}
                        >
                          <Play size={32} fill="white" style={{ color: 'white', marginLeft: '4px' }} />
                        </div>
                      </div>
                      <a
                        href={`https://www.youtube.com/watch?v=${video.video_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-2 right-2 p-2 rounded-lg transition-colors"
                        style={{ background: 'rgba(0,0,0,0.6)' }}
                      >
                        <ExternalLink size={16} style={{ color: '#F8FAFC' }} />
                      </a>
                    </div>
                  ) : (
                    <div 
                      className="flex items-center justify-center rounded-lg"
                      style={{ 
                        aspectRatio: '16/9',
                        background: 'rgba(15, 23, 42, 0.6)',
                        border: '1px dashed rgba(0, 240, 255, 0.2)'
                      }}
                    >
                      <span style={{ color: '#64748B' }}>{t('admin.youtube.noPreview')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Title Fields Column */}
              <div className="space-y-4">
                {/* English Title */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                    {t('admin.youtube.titleEn')}
                  </label>
                  <input
                    type="text"
                    value={video.title}
                    onChange={(e) => handleTitleChange(index, 'title', e.target.value)}
                    placeholder="Video title in English"
                    className="w-full px-4 py-3 rounded-lg transition-all duration-300"
                    style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(0, 240, 255, 0.2)',
                      color: '#F8FAFC'
                    }}
                    data-testid={`video-title-en-${index}`}
                  />
                </div>

                {/* Turkish Title */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                    {t('admin.youtube.titleTr')}
                  </label>
                  <input
                    type="text"
                    value={video.title_tr}
                    onChange={(e) => handleTitleChange(index, 'title_tr', e.target.value)}
                    placeholder="Video başlığı Türkçe"
                    className="w-full px-4 py-3 rounded-lg transition-all duration-300"
                    style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(0, 240, 255, 0.2)',
                      color: '#F8FAFC'
                    }}
                    data-testid={`video-title-tr-${index}`}
                  />
                </div>

                {/* Video ID Display */}
                {video.video_id && (
                  <div className="p-3 rounded-lg" style={{ background: 'rgba(0, 240, 255, 0.1)' }}>
                    <span className="text-sm" style={{ color: '#94A3B8' }}>Video ID: </span>
                    <span className="text-sm font-mono" style={{ color: '#00F0FF' }}>{video.video_id}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Note */}
      <div 
        className="p-4 rounded-lg flex items-start gap-3"
        style={{ background: 'rgba(0, 240, 255, 0.1)', border: '1px solid rgba(0, 240, 255, 0.2)' }}
      >
        <AlertCircle size={20} style={{ color: '#00F0FF', flexShrink: 0, marginTop: '2px' }} />
        <p className="text-sm" style={{ color: '#94A3B8' }}>
          {t('admin.common.turkish') === 'Turkish (TR)' 
            ? 'Videos will be displayed on the homepage in the "Featured from Our Channel" section. Thumbnails are automatically generated from YouTube.'
            : 'Videolar ana sayfada "Kanalımızdan Öne Çıkanlar" bölümünde gösterilecektir. Thumbnail\'ler otomatik olarak YouTube\'dan alınır.'
          }
        </p>
      </div>
    </div>
  );
};

export default AdminYouTube;
